import {getSessionGeneration, onSessionGenerationChange} from '../../lib/sessionGeneration.js'

// Ephemeral coordination only: no file, identity, preview token or permission is
// persisted. A returning view waits for its session's dispatched write before GET.
const pendingApplies = new Map()
onSessionGenerationChange(() => pendingApplies.clear())
export const IDENTITY_FILE_MAX_BYTES = 60000
const hash = value => typeof value === 'string' && /^[a-f0-9]{64}$/.test(value)
const version = value => Number.isSafeInteger(value) && value >= 0
const text = value => typeof value === 'string' && value.length > 0
const date = value => text(value) && Number.isFinite(Date.parse(value))
const actor = value => value && text(value.id) && typeof value.name === 'string'
const clone = value => JSON.parse(JSON.stringify(value))
const invalid = message => {throw new Error(message)}

function entriesValid(entries, count) {
  return Array.isArray(entries) && Number.isSafeInteger(count) && count === entries.length
    && entries.every(entry => text(entry?.portalId) && typeof entry.clickupId === 'string' && /^\d+$/.test(entry.clickupId)
      && text(entry.canonicalEmail) && Array.isArray(entry.providerEmailAliases) && entry.providerEmailAliases.every(text)
      && text(entry.matchBasis) && ['ready', 'held'].includes(entry.status)
      && (entry.status === 'ready' ? entry.heldReason === null : text(entry.heldReason))
      && (entry.currentPortal === null || (text(entry.currentPortal?.id) && typeof entry.currentPortal.name === 'string'
        && (entry.currentPortal.email === null || typeof entry.currentPortal.email === 'string') && text(entry.currentPortal.role) && typeof entry.currentPortal.active === 'boolean')))
}
export function validateIdentityRead(data) {
  if (data?.ok !== true || !version(data.version) || !entriesValid(data.entries, data.entryCount)
    || typeof data.capabilities?.canApply !== 'boolean' || !Array.isArray(data.audit) || typeof data.auditTruncated !== 'boolean'
    || data.providerQualifications?.activeFlagProvided !== false || data.providerQualifications?.emailVerifiedFlagProvided !== false
    || (data.version === 0 ? data.appliedAt !== null || data.appliedBy !== null || data.inputSha256 !== null || data.entryCount !== 0 : !date(data.appliedAt) || !text(data.appliedBy) || !hash(data.inputSha256))
    || data.audit.some(item => !version(item?.version) || !actor(item.actor) || !date(item.occurredAt) || typeof item.reason !== 'string'
      || (item.inputSha256 !== null && !hash(item.inputSha256)) || (item.declaredApprovalAt !== null && !date(item.declaredApprovalAt)) || (item.declaredApprovalMessageId !== null && !text(item.declaredApprovalMessageId)))) {
    invalid('The saved identity map or current authority could not be verified. Reload before making changes.')
  }
  return clone(data)
}
export function validateIdentityPreview(data, file, expectedVersion, now = Date.now()) {
  const p = data?.preview
  if (data?.ok !== true || !p || p.inputSha256 !== file.inputSha256 || p.expectedVersion !== expectedVersion
    || p.currentVersion !== expectedVersion || !entriesValid(p.entries, p.entryCount) || typeof p.ready !== 'boolean'
    || !date(p.expiresAt) || Date.parse(p.expiresAt) <= now
    || (p.ready ? !text(p.previewToken) || p.entries.some(entry => entry.status !== 'ready') : p.previewToken !== null)
    || p.approvalProvenance?.source !== 'uploaded_document_metadata_not_authorization'
    || !date(p.approvalProvenance.declaredApprovedAt) || !text(p.approvalProvenance.declaredApprovalMessageId)) {
    invalid('The preview does not match this exact file and saved version. Reload and preview again.')
  }
  return clone(p)
}
export function validateIdentityReceipt(data, attempt, count) {
  const r = data?.receipt
  if (data?.ok !== true || !r || !((data.applied === true && data.idempotent === false) || (data.applied === false && data.idempotent === true))
    || !version(r.version) || r.inputSha256 !== attempt.inputSha256 || r.entryCount !== count || !date(r.appliedAt) || !text(r.appliedBy)
    || r.version !== attempt.expectedVersion + 1) {
    invalid('No reliable matching apply receipt was received.')
  }
  return clone(r)
}
export async function readIdentityFile(file, cryptoProvider = globalThis.crypto) {
  if (!file || typeof file.arrayBuffer !== 'function' || !Number.isSafeInteger(file.size) || file.size < 1 || file.size > IDENTITY_FILE_MAX_BYTES) {
    invalid('Select a non-empty UTF-8 JSON file no larger than 60,000 bytes.')
  }
  const bytes = new Uint8Array(await file.arrayBuffer())
  if (bytes.byteLength !== file.size || bytes.byteLength > IDENTITY_FILE_MAX_BYTES) invalid('The file changed while being read. Select it again.')
  let rawInput
  try {rawInput = new TextDecoder('utf-8', {fatal: true, ignoreBOM: true}).decode(bytes)}
  catch {invalid('The file is not valid UTF-8. No preview was sent.')}
  // No trimming, parsing/re-serialization, BOM removal or newline conversion.
  try {JSON.parse(rawInput)} catch {invalid('The selected file is not valid JSON. No preview was sent.')}
  if (!cryptoProvider?.subtle) invalid('Secure file hashing is unavailable. Use this portal over HTTPS.')
  const digest = await cryptoProvider.subtle.digest('SHA-256', bytes)
  const inputSha256 = Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('')
  return {name: String(file.name || 'Selected JSON file'), size: bytes.byteLength, rawInput, inputSha256}
}
export const identityInitialState = () => ({phase: 'loading', busy: false, data: null, file: null, preview: null, reason: '', error: '', message: ''})
export const identityCanEdit = state => !state.busy && ['ready', 'preview'].includes(state.phase) && state.data?.capabilities?.canApply === true
export const identityCanApply = (state, now = Date.now()) => identityCanEdit(state) && state.preview?.ready === true
  && state.preview.inputSha256 === state.file?.inputSha256 && state.preview.expectedVersion === state.data.version
  && Date.parse(state.preview.expiresAt) > now && Boolean(state.reason.trim()) && state.reason.length <= 500

export function createIdentityController({api: client, onState = () => {}, readFile = readIdentityFile, now = Date.now, writes = pendingApplies}) {
  const session = getSessionGeneration()
  let state = identityInitialState(), disposed = false, epoch = 0, pending = null
  const live = token => !disposed && session === getSessionGeneration() && (!token || token.epoch === epoch)
  const publish = patch => {if (live()) {state = {...state, ...patch}; onState(state)}}
  const begin = (kind, patch) => {
    if (pending?.kind !== 'apply') pending?.abort?.abort()
    const token = {epoch: ++epoch, kind, abort: new AbortController()}
    pending = token; publish({busy: true, error: '', message: '', ...patch}); return token
  }
  const end = token => {if (pending === token) {pending = null; publish({busy: false})}}
  const clear = {file: null, preview: null, reason: ''}
  const readback = async (token, attempt = null, receipt = null) => {
    const data = validateIdentityRead(await client.teamClickupIdentities({signal: token.abort.signal}))
    if (!live(token)) return null
    if (!attempt) {publish({phase: 'ready', data, error: ''}); return data}
    const matches = data.inputSha256 === attempt.inputSha256 && data.version === (receipt?.version ?? attempt.expectedVersion + 1)
      && (!receipt || (data.entryCount === receipt.entryCount && data.appliedBy === receipt.appliedBy && data.appliedAt === receipt.appliedAt))
    if (matches) publish({phase: 'ready', data, error: '', message: `Saved mapping verified by server readback at version ${data.version}. No roles, email addresses or CRM access changed.`})
    else publish({phase: 'uncertain', data, error: 'This readback does not confirm the submitted mapping. Review the saved version and history; do not replay Apply blindly.', message: ''})
    return data
  }
  const unsubscribe = onSessionGenerationChange(() => {
    epoch++; pending?.kind !== 'apply' && pending?.abort.abort()
    state = {...identityInitialState(), phase: 'denied', error: 'The signed-in session changed. Reload in the current session.'}
    onState(state)
  })
  return {
    snapshot: () => state,
    async load() {
      if (!live() || pending?.kind === 'apply' || pending?.kind === 'read') return null
      const token = begin('read', {phase: 'loading', data: null, ...clear})
      try {
        const prior = writes.get(session)
        if (prior) {publish({message: 'Waiting for the earlier Apply to settle before reading the saved map.'}); await prior.promise.catch(() => {})}
        if (!live(token)) return null
        const reconcile = prior && !prior.rejected ? prior : null
        const data = await readback(token, reconcile, reconcile?.receipt || null)
        if (live(token) && state.phase === 'ready' && writes.get(session) === prior) writes.delete(session)
        return data
      } catch (error) {
        if (live(token)) publish({phase: [401, 403].includes(error.status) ? 'denied' : 'unavailable', data: null, error: error.message || 'Saved mapping unavailable; this is not an empty map.', message: ''})
        return null
      } finally {end(token)}
    },
    async selectFile(file) {
      if (!live() || pending?.kind === 'apply' || !state.data?.capabilities?.canApply
        || !['ready', 'preview', 'file', 'previewing'].includes(state.phase)) return null
      const token = begin('file', {phase: 'file', ...clear})
      try {
        const selected = await readFile(file)
        if (!live(token)) return null
        publish({phase: 'ready', file: selected}); return selected
      } catch (error) {if (live(token)) publish({phase: 'ready', error: error.message || 'File could not be read.'}); return null}
      finally {end(token)}
    },
    reason(value) {
      if (!live() || !identityCanEdit(state) || typeof value !== 'string' || value.length > 500) return false
      publish({reason: value}); return true
    },
    cancel() {
      if (!live() || !state.data || !['ready', 'preview', 'file', 'previewing'].includes(state.phase)) return false
      epoch++; pending?.abort.abort(); pending = null
      publish({phase: 'ready', busy: false, ...clear, error: '', message: 'Local file and preview discarded. The saved map was not changed.'}); return true
    },
    async preview() {
      if (!live() || !identityCanEdit(state) || !state.file || writes.has(session)) return null
      const file = state.file, expectedVersion = state.data.version
      const token = begin('preview', {phase: 'previewing', preview: null})
      try {
        const result = await client.teamClickupIdentityPreview({rawInput: file.rawInput, inputSha256: file.inputSha256, expectedVersion}, {signal: token.abort.signal})
        if (!live(token)) return null
        const preview = validateIdentityPreview(result, file, expectedVersion, now())
        publish({phase: 'preview', preview}); return preview
      } catch (error) {
        if (live(token)) publish({phase: error.status === 409 ? 'conflict' : [401, 403].includes(error.status) ? 'denied' : 'ready', preview: null,
          error: error.status === 409 ? 'The version or preview changed. Reload the saved map and preview the file again.' : error.message || 'Preview unavailable. Nothing was applied.',
          ...([401, 403].includes(error.status) ? {data: null, ...clear} : {})})
        return null
      } finally {end(token)}
    },
    async apply() {
      if (!live() || !identityCanApply(state, now()) || writes.has(session)) {
        if (live() && state.preview && Date.parse(state.preview.expiresAt) <= now()) publish({preview: null, phase: 'ready', error: 'The preview expired. Preview this file again before Apply.'})
        return null
      }
      const body = {rawInput: state.file.rawInput, inputSha256: state.file.inputSha256, expectedVersion: state.preview.expectedVersion, previewToken: state.preview.previewToken, reason: state.reason.trim()}
      const count = state.preview.entryCount
      const token = begin('apply', {phase: 'applying', ...clear})
      const attempt = {inputSha256: body.inputSha256, expectedVersion: body.expectedVersion, receipt: null, promise: null}
      // Invoke synchronously after the fence. No delayed microtask can start a
      // mutation after navigation, cancellation or a new sign-in.
      try {attempt.promise = Promise.resolve(client.teamClickupIdentityApply(body))}
      catch (error) {attempt.promise = Promise.reject(error)}
      writes.set(session, attempt)
      let denied = false
      try {
        const result = await attempt.promise
        attempt.receipt = validateIdentityReceipt(result, attempt, count)
      } catch (error) {
        if ([400, 401, 403, 404, 409, 422].includes(error.status)) {
          denied = true
          attempt.rejected = true
          if (!live(token)) return null
          publish({phase: error.status === 409 ? 'conflict' : [401, 403].includes(error.status) ? 'denied' : 'unavailable', data: null,
            error: error.status === 409 ? 'The version or preview is stale. Reload and create a new preview; nothing was overwritten by this request.' : `Apply was refused. ${error.message || 'Reload current authority before another preview.'}`})
          end(token)
          return null
        }
        if (!live(token)) return null
        publish({message: 'The Apply response was uncertain. Reading the saved map, not retrying the write.'})
      } finally {
        if (denied && writes.get(session) === attempt) writes.delete(session)
      }
      if (!live(token)) return null
      publish({phase: 'reconciling', preview: null})
      try {return await readback(token, attempt, attempt.receipt)}
      catch (error) {
        if (live(token)) publish({phase: [401, 403].includes(error.status) ? 'denied' : 'uncertain', data: null,
          error: `Apply outcome is not confirmed. ${error.message || 'Readback failed.'} Reload the saved map; do not apply again blindly.`, message: ''})
        return null
      } finally {
        // Keep only minimal reconciliation metadata after an uncertain outcome.
        // Raw file bytes and preview tokens are never retained in this registry.
        if (live(token) && state.phase === 'ready' && writes.get(session) === attempt) writes.delete(session)
        end(token)
      }
    },
    dispose() {disposed = true; epoch++; if (pending?.kind !== 'apply') pending?.abort.abort(); unsubscribe(); state = identityInitialState()},
  }
}
